import React, { useState, useCallback, useMemo } from "react";
import "./LearningPlans.css";
import ValueSelector from "./components/ValueSelector";
import CompetencyTable from "./components/CompetencyTable";
import {
  useGetMyDevelopmentPlanQuery,
  useGetDevelopmentPlanValuesQuery,
  useCreateDevelopmentPlanMutation,
  useUpdateDevelopmentPlanMutation,
  useUpdateDevelopmentPlanEntryMutation,
} from "../../services/api";

export default function DevelopmentPlans() {
  const [pendingValue, setPendingValue] = useState("");

  // API hooks
  const { data: plan, isLoading, error } = useGetMyDevelopmentPlanQuery();
  const { data: valuesData, isLoading: isLoadingValues } = useGetDevelopmentPlanValuesQuery();
  const [createPlan, { isLoading: isCreating }] = useCreateDevelopmentPlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdateDevelopmentPlanMutation();
  const [updateEntry, { isLoading: isSavingEntry }] = useUpdateDevelopmentPlanEntryMutation();

  // Extract values array from API response
  const values = valuesData?.values || [];

  // Get development goals for the selected value's competencies (no longer needed with new format)
  const selectedValueData = useMemo(() => {
    if (!plan?.selectedValue || !values.length) return null;
    return values.find(v => v.name === plan.selectedValue);
  }, [plan?.selectedValue, values]);

  // Group entries by competencyIndex for accordion display
  // Each entry is a goal entry now, so we group them by competency
  const entriesByCompetency = useMemo(() => {
    if (!plan?.competencyEntries) return {};
    return plan.competencyEntries.reduce((acc, entry, entryIndex) => {
      const key = entry.competencyIndex;
      if (!acc[key]) acc[key] = [];
      // Include the flat array index for saving
      acc[key].push({ ...entry, entryIndex });
      return acc;
    }, {});
  }, [plan?.competencyEntries]);

  const handleValueChange = async (newValue) => {
    if (!newValue) return;

    if (plan) {
      // Update existing plan with new value
      try {
        await updatePlan({ selectedValue: newValue }).unwrap();
      } catch (err) {
        console.error("Failed to update plan:", err);
        alert("Failed to update plan. Please try again.");
      }
    } else {
      // Create new plan
      setPendingValue(newValue);
      try {
        await createPlan({ selectedValue: newValue }).unwrap();
        setPendingValue("");
      } catch (err) {
        console.error("Failed to create plan:", err);
        setPendingValue("");
        alert("Failed to create plan. Please try again.");
      }
    }
  };

  const handleEntrySave = useCallback(async (competencyIndex, data) => {
    try {
      await updateEntry({ competencyIndex, ...data }).unwrap();
    } catch (err) {
      console.error("Failed to save entry:", err);
      throw err; // Re-throw so CompetencyTable can handle it
    }
  }, [updateEntry]);

  const isBusy = isCreating || isUpdating;
  const isPageLoading = isLoading || isLoadingValues;

  return (
    <div className="learning-container">
      <button className="welcome">← Back Home</button>
      <div className="page-header">
        <h1 className="username">Development Plans</h1>
        {plan && (
          <div className="page-header-actions">
            <button className="download-pdf-btn">Download Pdf →</button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isPageLoading && (
        <div className="development-loading">
          Loading your development plan...
        </div>
      )}

      {/* Error State */}
      {error && error.status !== 404 && (
        <div className="development-error">
          Failed to load development plan. Please try again later.
        </div>
      )}

      {/* Value Selector */}
      {!isPageLoading && (
        <div className="section-card">
          <h2 className="section-title">Select Your Focus Area</h2>
          <ValueSelector
            value={plan?.selectedValue || pendingValue}
            onChange={handleValueChange}
            disabled={isBusy}
            values={values}
          />
        </div>
      )}

      {/* Competency Tables - Accordion Layout */}
      {plan && plan.competencyEntries && plan.competencyEntries.length > 0 && (
        <div className="section-card">
          <h2 className="section-title">
            Leadership Development Plans - {plan.selectedValue}
          </h2>
          <p className="section-subtitle">
            Click on each competency to expand and fill out your development plan. Each goal has its own row with separate fields.
          </p>

          <div className="dev-accordion-container">
            {Object.entries(entriesByCompetency)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([compIndex, goalEntries]) => (
                <CompetencyTable
                  key={`${plan.selectedValue}-comp-${compIndex}`}
                  competencyName={goalEntries[0].competencyName}
                  competencyIndex={Number(compIndex)}
                  goalEntries={goalEntries}
                  onSave={handleEntrySave}
                  isSaving={isSavingEntry}
                />
              ))}
          </div>
        </div>
      )}

      {/* Empty Goals State - Plan exists but no goals defined */}
      {plan && (!plan.competencyEntries || plan.competencyEntries.length === 0) && (
        <div className="section-card empty-state">
          <div className="empty-state-content">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9CA3AF"
              strokeWidth="1.5"
            >
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3>No Development Goals Defined</h3>
            <p>No development goals have been created for "{plan.selectedValue}" yet. Please contact your administrator to set up development goals.</p>
          </div>
        </div>
      )}

      {/* Empty State - No plan selected yet */}
      {!isPageLoading && !plan && !pendingValue && (
        <div className="section-card empty-state">
          <div className="empty-state-content">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9CA3AF"
              strokeWidth="1.5"
            >
              <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3>No Development Plan Yet</h3>
            <p>Select a value above to start building your development plan.</p>
          </div>
        </div>
      )}
    </div>
  );
}
